import { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';

interface CopyButtonProps {
  text: string;
  className?: string;
}

function CopyButton({ text, className = '' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const { lang } = useTranslation();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // E7 / audit F-10: don't pollute the user's console with errors
      // they can do nothing about. Browsers block clipboard.writeText
      // outside HTTPS or in some embeds — surface as a no-op to the
      // user instead.
    }
  }, [text]);

  // F1: i18n. Falls back to English when the key is missing in the
  // active dictionary so dev environments + new languages still work.
  const copiedLabel = lang.get('msg.copied') || 'Copied';
  const copyLabel = lang.get('msg.copy') || 'Copy';
  const copiedAria =
    lang.get('msg.copyButtonCopiedAria') || 'Copied to clipboard';
  const copyAria = lang.get('msg.copyButtonCopyAria') || 'Copy to clipboard';

  return (
    <button
      onClick={handleCopy}
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors ${className}`}
      title={copied ? copiedLabel : copyAria}
      // E7 / audit F-10: aria-label updates with state so screen
      // readers announce the success transition. role+aria-live on a
      // sibling sr-only span lets the announcement happen WITHOUT the
      // visible-text ping screen readers would otherwise miss when the
      // button text changes.
      aria-label={copied ? copiedAria : copyAria}
      type="button"
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          <span>{copiedLabel}</span>
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          <span>{copyLabel}</span>
        </>
      )}
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? copiedAria : ''}
      </span>
    </button>
  );
}

export default CopyButton;
